import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { ChangePasswordForm } from '@ecom/ui-kit';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useChangePasswordMutation } from '../api';
import { AuthLayout } from '../components/AuthLayout';
import { StatusIcon } from '../components/StatusIcon';

import { ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * The screen a provisional password leads to - and the way to change one's password at any time.
 *
 * Reached (1) straight after a sign-in whose answer says `mustChangePassword`, and (2) from anywhere when the API answers
 * `PASSWORD_CHANGE_REQUIRED`. The person types the provisional password they were given and a password of their own; on success the
 * API has ended EVERY earlier session and returned a fresh one, which replaces this one. Nothing is kept: the passwords live in the
 * form's own state and the mutation is collected at once.
 */
export function ChangePasswordPage(): ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mustChangePassword, setSession, clearSession } = useSession();
  const mutation = useChangePasswordMutation();
  const [error, setError] = useState<string | null>(null);
  /** The password that opened this session is a provisional one: this is the merchant's FIRST step, not a routine change. */
  const provisional = mustChangePassword;

  async function onSubmit(values: { currentPassword: string; newPassword: string }): Promise<void> {
    setError(null);
    try {
      const fresh = await mutation.mutateAsync(values);
      mutation.reset();
      // Everything cached belongs to a session that no longer exists.
      queryClient.clear();
      setSession({
        accessToken: fresh.accessToken,
        refreshToken: fresh.refreshToken,
        mustChangePassword: fresh.mustChangePassword,
      });
      // The welcome card on the dashboard says « Mot de passe enregistré. » (nothing sensitive in the navigation state).
      void navigate('/dashboard', {
        replace: true,
        state: provisional ? { passwordSaved: true } : null,
      });
    } catch (failure) {
      mutation.reset();
      // A wrong PROVISIONAL password has its own sentence (it was handed over on paper or by word of mouth: say how to type it, and
      // that repeated errors lock the account); an ordinary change keeps the usual one.
      setError(
        provisional &&
          failure instanceof ApiError &&
          failure.errorCode === 'CURRENT_PASSWORD_INVALID'
          ? t('auth.changePassword.provisionalWrong')
          : t(resolveErrorMessageKey(failure)),
      );
      throw failure;
    }
  }

  return (
    <AuthLayout
      icon={<StatusIcon kind="lock" />}
      title={provisional ? t('auth.changePassword.welcomeTitle') : t('auth.changePassword.title')}
      subtitle={
        provisional ? t('auth.changePassword.welcomeSubtitle') : t('common.changePassword.rules')
      }
      footer={
        <button
          type="button"
          className="font-medium text-accent hover:underline"
          onClick={() => {
            clearSession();
            void navigate('/login', { replace: true });
          }}
        >
          {t('auth.changePassword.signOut')}
        </button>
      }
    >
      {provisional ? (
        <>
          <ol
            aria-label={t('auth.changePassword.steps')}
            className="mb-4 flex items-center justify-between gap-2 text-xs"
          >
            <li className="flex items-center gap-1 text-good">
              <span aria-hidden="true">✓</span> {t('auth.changePassword.step1')}
            </li>
            <li aria-current="step" className="flex items-center gap-1 font-semibold text-ink">
              <span aria-hidden="true">②</span> {t('auth.changePassword.step2')}
            </li>
            <li className="flex items-center gap-1 text-inkFaint">
              <span aria-hidden="true">③</span> {t('auth.changePassword.step3')}
            </li>
          </ol>
          <p className="mb-4 text-sm text-inkSoft">{t('auth.changePassword.rule')}</p>
        </>
      ) : null}
      <ChangePasswordForm
        onSubmit={onSubmit}
        error={error}
        busy={mutation.isPending}
        {...(mustChangePassword ? { currentLabel: t('auth.changePassword.currentLabel') } : {})}
      />
    </AuthLayout>
  );
}
